import React, { ChangeEvent, useContext, useEffect, useMemo, useState } from "react";
import IErrorDTO from "../../../models/DTO/IErrorDTO";
import SupplierContext from "../../../contexts/SupplierContext";
import UserContext from "../../../contexts/UserContext";
import IOrder_Item_Approval_Queue from "../../../models/IOrder_Item_Approval_Queue";
import EndpointsEnum from "../../../models/EndpointsEnum";
import IOrderEntryOnGetOrderItemPriceApprovalsDTO from "../../../models/DTO/IOrderEntryOnGetOrderItemPriceApprovalsDTO";
import IOrderEntryGetOrderItemPriceApprovalsDTO from "../../../models/DTO/IOrderEntryGetOrderItemPriceApprovalsDTO";
import { OwIconButton } from "../../shared/OwIconButton/OwIconButton";
import IOwTableCols from "../../../models/IOwTableCols";
import { NumericFormat } from "react-number-format";
import { Link } from "react-router-dom";
import IOrderEntryProcessOrderItemPriceApprovalDTO from "../../../models/DTO/IOrderEntryProcessOrderItemPriceApprovalDTO";
import IOrderEntryOnProcessOrderItemPriceApprovalDTO from "../../../models/DTO/IOrderEntryOnProcessOrderItemPriceApprovalDTO";
import INotification from "../../../models/INotification";
import NotificationLevelEnum from "../../../models/NotificationLevelEnum";
import NotificationPositionEnum from "../../../models/NotificationPositionEnum";
import { OwFnInput } from "../../shared/OwFnInput/OwFnInput";
import OwLabel from "../../shared/OwLabel/OwLabel";
import styled from "styled-components";
import OwTitle from "../../shared/OwTitle/OwTitle";
import TitleTagEnum from "../../../models/TitleTagEnum";
import OwButton from "../../shared/OwButton/OwButton";
import { OwDivTable } from "../../shared/OwDivTable/OwDivTable";
import IOwDivTableCols from "../../shared/OwDivTable/IOwDivTableCols";
import { v4 } from "uuid";
import { Socket } from "socket.io-client";
import IUiSocketSetup from "../../../models/IUiSocketSetup";
import IBreadcrumbs from "../../../models/IBreadcrumbs";
import Breadcrumbs from "../../shared/Breadcrumbs/Breadcrumbs";
import OwToggle from "@components/shared/OwToggle/OwToggle";
import { ControlBar } from "@components/shared/ControlBar/ControlBar";

type Props = {
    isOnline: boolean;
    socket: Socket;
    toggleSidebar: (shouldBeVisible?: boolean) => void;
    setSidebarComponent: (component: any) => void;
    setActivityIndicator: (activityOn: boolean) => void;
    notifyFn: (data: IErrorDTO | null, _notification?: any, isFormatted?: boolean) => void;
};

interface IApprovedAmount {
    /**
     * Key is the item guid
     */
    [key: string]: {
        amount: number;
    };
}

export const PriceApproval: React.FC<Props> = (props) => {
    const supplierModel = useContext(SupplierContext);
    const userModel = useContext(UserContext);

    const getTableCols = (): IOwDivTableCols[] => {
        return [
            {
                title: "",
                renderFn: renderActions,
            },
            {
                title: "amount",
                renderFn: renderAmount,
            },
            {
                title: "item",
                renderFn: renderItem,
            },
            {
                title: "order",
                renderFn: renderOrder,
            },
            {
                title: "user",
                renderFn: renderUser,
            },
        ];
    };

    const getBreadcrumbs = (): IBreadcrumbs[] => {
        return [
            {
                title: "orders",
                url: "/orders",
            },
            {
                title: "price_approval",
                url: "/orders/price_approval",
            },
        ];
    };

    const [approvalItems, setApprovalItems] = useState<IOrder_Item_Approval_Queue[]>([]);
    const [isDataFetching, setIsDataFetching] = useState(false);
    const [isInitialData, setIsInitialData] = useState(true);
    const [approvedAmounts, setApprovedAmounts] = useState<IApprovedAmount>();
    const [tableCols, setTableCols] = useState<IOwDivTableCols[]>();
    const crumbs = useMemo(() => getBreadcrumbs(), []);
    const [autoSubmitOnAllApproved, setAutoSubmitOnAllApproved] = useState(true);

    const onGetOrderItemPriceApprovals = (data: IOrderEntryOnGetOrderItemPriceApprovalsDTO) => {
        const _approvedAmounts = getApprovedAmounts(data.approvalItems);
        setTableCols([]);
        setApprovedAmounts(_approvedAmounts);
        setApprovalItems(data.approvalItems);
        setIsInitialData(false);
        setIsDataFetching(false);
        setTableCols(getTableCols());

        props.setActivityIndicator(false);
    };

    const onProcessItem = (data: IOrderEntryOnProcessOrderItemPriceApprovalDTO) => {
        const notification: INotification = {
            message: "successfully_processed_item_approval",
            level: NotificationLevelEnum.success,
            position: NotificationPositionEnum.bc,
        };

        props.notifyFn(null, notification);
        props.setActivityIndicator(false);

        getOrderItemPriceApprovals();
    };

    const sockets: IUiSocketSetup[] = [
        { endpoint: EndpointsEnum.OrderEntryOnGetOrderItemPriceApprovals, fn: onGetOrderItemPriceApprovals },
        { endpoint: EndpointsEnum.OrderEntryOnProcessOrderItemPriceApproval, fn: onProcessItem },
    ];

    useEffect(() => {
        for (const socket of sockets) {
            props.socket.on(socket.endpoint, socket.fn);
        }

        getOrderItemPriceApprovals();

        return () => {
            for (const socket of sockets) {
                props.socket.removeListener(socket.endpoint, socket.fn);
            }
        };
    }, []);

    const getApprovedAmounts = (_approvalItems: IOrder_Item_Approval_Queue[]) => {
        const _approvedAmounts: IApprovedAmount = { ...approvedAmounts };

        for (const item of _approvalItems) {
            let found = false;

            if (_approvedAmounts[item.guid] && _approvedAmounts[item.guid].amount >= 0) {
                found = true;
            }

            if (!found) {
                let amount = item.requested_amount || 0;

                if (item.approved_amount && item.approved_amount > 0) {
                    amount = item.approved_amount;
                }
                console.log("Amount: ", amount, item.guid);

                _approvedAmounts[item.guid] = {
                    amount,
                };
            }
        }

        return _approvedAmounts;
    };

    useEffect(() => {
        if (approvalItems.length) {
            const _approvedAmounts = getApprovedAmounts(approvalItems);

            console.log("approvedAmounts", _approvedAmounts);

            setApprovedAmounts(_approvedAmounts);
        }
    }, [approvalItems]);

    const handleAmountChange = (e: ChangeEvent<any>, item: IOrder_Item_Approval_Queue) => {
        const _approvedAmounts = { ...approvedAmounts };

        _approvedAmounts[item.guid].amount = Number(e.target.value || 0);

        setApprovedAmounts(_approvedAmounts);
    };

    const renderActions = (item: IOrder_Item_Approval_Queue) => {
        const approveBtn = (
            <OwButton
                leftIconClass={"fas fa-check"}
                title={"approve"}
                className={"btn-success"}
                fn={() => {
                    processItem(item, true);
                }}
            />
        );

        const rejectBtn = (
            <OwButton
                leftIconClass={"fas fa-times"}
                title={"reject"}
                className={"btn-danger"}
                fn={() => {
                    processItem(item, false);
                }}
            />
        );

        return (
            <div className={"d-flex"}>
                <ControlBar
                    name={"Price Approval Actions"}
                    sections={[
                        { name: "Approve", elements: [approveBtn] },
                        { name: "Reject", elements: [rejectBtn] },
                    ]}
                />
            </div>
        );
    };

    const renderOrder = (item: IOrder_Item_Approval_Queue) => {
        return (
            <div>
                <Link to={`/orders/${item.Order.guid}/entry`}>{item.Order.name}</Link>
                <div>{item.Order.Vendor?.name}</div>
            </div>
        );
    };

    const renderItem = (item: IOrder_Item_Approval_Queue) => {
        return <div>{item.Order_Item?.Catalog_Item?.name}</div>;
    };

    const renderUser = (item: IOrder_Item_Approval_Queue) => {
        return (
            <div>
                <div>{item.Requested_User.name}</div>
                <div>
                    <a href={`mailto:${item.Requested_User.email}`}>{item.Requested_User.email}</a>
                </div>
            </div>
        );
    };

    const getApprovedAmount = (item: IOrder_Item_Approval_Queue): number => {
        if (approvedAmounts) {
            return approvedAmounts[item.guid] ? approvedAmounts[item.guid].amount : 0;
        } else {
            return 0;
        }
    };

    const getApprovedAmountFnInput = (item: IOrder_Item_Approval_Queue) => {
        const amountValue = getApprovedAmount(item);

        return (
            <OwFnInput
                fn={(e: ChangeEvent<any>) => {
                    handleAmountChange(e, item);
                }}
                name={"approved_amount"}
                value={(approvedAmounts && approvedAmounts[item.guid].amount) || 0}
            />
        );
    };

    const getStandardPrice = (item: IOrder_Item_Approval_Queue) => {
        const priceGroup = item.Order?.Price_Group?.value;

        let rejectPrice = 0;

        if (priceGroup) {
            rejectPrice = item.Order_Item.Catalog_Item?.pricing[priceGroup];
        }

        return (
            <NumericFormat
                value={rejectPrice}
                decimalScale={supplierModel.pricing_precision || 2}
                fixedDecimalScale={true}
                prefix={"$"}
                displayType={"text"}
            />
        );
    };

    const getRejectedPriceDetail = (item: IOrder_Item_Approval_Queue) => {
        let rejectPrice = 0;

        if (item.Order_Item.Catalog_Item?.min_price) {
            rejectPrice = item.Order_Item.Catalog_Item.min_price;
        }

        return (
            <NumericFormat
                value={rejectPrice}
                decimalScale={supplierModel.pricing_precision || 2}
                fixedDecimalScale={true}
                prefix={"$"}
                displayType={"text"}
            />
        );
    };

    const renderAmount = (item: IOrder_Item_Approval_Queue) => {
        return (
            <div>
                <div className={"d-flex"}>
                    <OwLabel title={"requested_price"} postText={":"} className={"w-10r"} />
                    <NumericFormat
                        value={item.requested_amount}
                        decimalScale={supplierModel.pricing_precision || 2}
                        fixedDecimalScale={true}
                        prefix={"$"}
                        displayType={"text"}
                    />
                </div>
                <div className={"d-flex align-items-center"}>
                    <OwLabel title={"approved_price"} postText={":"} className={"w-10r"} />
                    <OwFnInput
                        fn={(e: ChangeEvent<any>) => {
                            handleAmountChange(e, item);
                        }}
                        type={"number"}
                        name={"approved_amount"}
                        value={(approvedAmounts && approvedAmounts[item.guid].amount) || 0}
                        debounceMs={500}
                        enableDebounce={true}
                    />
                </div>
                <div>
                    <OwLabel title={"reject_price"} postText={":"} className={"w-10r"} />
                    {getRejectedPriceDetail(item)}
                </div>
                <div>
                    <OwLabel title={"standard_price"} postText={":"} className={"w-10r"} />
                    {getStandardPrice(item)}
                </div>
            </div>
        );
    };

    const getOrderItemPriceApprovals = () => {
        const payload: IOrderEntryGetOrderItemPriceApprovalsDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            filterApproved: true,
        };

        props.socket.emit(EndpointsEnum.OrderEntryGetOrderItemPriceApprovals, payload);

        setIsDataFetching(true);
    };

    const processItem = (item: IOrder_Item_Approval_Queue, isApproved: boolean) => {
        if (approvedAmounts) {
            const payload: IOrderEntryProcessOrderItemPriceApprovalDTO = {
                userGuid: userModel.guid,
                userToken: userModel.token,
                supplierGuid: supplierModel.guid,
                itemGuid: item.guid,
                approved: isApproved,
                approvedAmount: approvedAmounts[item.guid].amount || 0,
                submitOrderIfAllApproved: autoSubmitOnAllApproved,
            };

            props.socket.emit(EndpointsEnum.OrderEntryProcessOrderItemPriceApproval, payload);

            props.setActivityIndicator(true);
        }
    };

    return (
        <div className={"PricingApproval"}>
            <Breadcrumbs crumbs={crumbs} />

            <OwTitle title={"price_approval"} tag={TitleTagEnum.H2} className={"mt-4"} />

            <OwToggle
                wrapperClassName="mt-4"
                labelOnRight={true}
                label="auto_submit_on_all_approved"
                name="auto_submit_on_all_approved"
                fn={() => {
                    setAutoSubmitOnAllApproved(!autoSubmitOnAllApproved);
                }}
                active={autoSubmitOnAllApproved}
            />
            {/*{*/}
            {/*    tableCols? <OwDivTable*/}
            {/*        cols={tableCols}*/}
            {/*        data={approvalItems}*/}
            {/*        colClassName={"w-20"}*/}
            {/*    /> : null*/}
            {/*}*/}

            <div className={"OwDivTable shadow p-2"}>
                <TableHeader className={"border-bottom"}>
                    {tableCols?.map((col, index) => {
                        return (
                            <div key={v4()} className={"w-20"}>
                                {col.hideTitle ? null : <OwLabel title={col.title} isBold={true} />}
                            </div>
                        );
                    })}
                </TableHeader>
                {approvalItems.map((item) => {
                    return (
                        <TableRow key={v4()}>
                            <div className={"w-20"}>{renderActions(item)}</div>
                            <div className={"w-20"}>{renderAmount(item)}</div>
                            <div className={"w-20"}>{renderItem(item)}</div>
                            <div className={"w-20"}>{renderOrder(item)}</div>
                            <div className={"w-20"}>{renderUser(item)}</div>
                        </TableRow>
                    );
                })}
            </div>
        </div>
    );
};

const TableHeader = styled.div`
    display: flex;

    & > div {
        padding: 0.5rem;
    }
`;

const TableRow = styled.div`
    display: flex;

    & > div {
        padding: 0.5rem;
    }

    &:hover {
        background-color: var(--table-hover-row);
    }
`;
